import React from 'react';
import { Link } from 'react-router-dom';

import titleImg from '../../assets/img/logo-lockup.svg';

// Public data-deletion page for the Google Play "Data deletion" URL
// (https://italiannaturally.com/delete_data). Must name the app/developer,
// prominently show the deletion steps, and state what's deleted/kept +
// retention. Reachable without authentication.

const LAST_UPDATED = 'June 8, 2026';

const FONT_BODY = '"Hanken Grotesk", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const FONT_DISPLAY = '"Fredoka", system-ui, sans-serif';

const styles = {
	page: { fontFamily: FONT_BODY, color: '#352F27', lineHeight: 1.6, backgroundColor: '#FBF7F0', minHeight: '100vh' },
	header: {
		display: 'flex', alignItems: 'center', justifyContent: 'space-between',
		gap: 16, maxWidth: 760, margin: '0 auto', padding: '24px 24px 0',
	},
	logo: { height: 34 },
	back: { color: '#D86641', fontWeight: 700, textDecoration: 'none' },
	container: { maxWidth: 760, margin: '0 auto', padding: '8px 24px 64px' },
	h1: { fontFamily: FONT_DISPLAY, fontSize: 34, fontWeight: 600, marginBottom: 4, color: '#1E1A15' },
	updated: { color: '#6B5F4D', marginTop: 0 },
	h2: { fontFamily: FONT_DISPLAY, fontSize: 24, fontWeight: 600, marginTop: 36, color: '#1E1A15' },
	a: { color: '#D86641' },
	steps: {
		background: '#FBF0EB', borderLeft: '4px solid #D86641', borderRadius: 12,
		padding: '4px 20px', margin: '24px 0',
	},
};

const DeleteData = () => {
	return (
		<div style={styles.page}>
			<div style={styles.header}>
				<Link to="/">
					<img src={titleImg} alt="Italian Naturally" style={styles.logo} />
				</Link>
				<Link to="/" style={styles.back}>← Home</Link>
			</div>

			<div style={styles.container}>
				<h1 style={styles.h1}>Delete your data</h1>
				<p style={styles.updated}>Last updated: {LAST_UPDATED}</p>

				<p>
					<strong>Italian Naturally</strong> (operated by Andrew Bogaard) lets you
					request deletion of the personal data we hold about you. Because your data
					is tied to your account, the simplest path is to delete your account, which
					removes all of it — or you can ask us to delete specific data and keep your
					account.
				</p>

				<div style={styles.steps}>
					<p><strong>How to request deletion</strong></p>
					<ol>
						<li>
							Email{' '}
							<a style={styles.a} href="mailto:abogaard.dev@gmail.com?subject=Delete%20my%20data">
								abogaard.dev@gmail.com
							</a>{' '}
							<strong>from the email address on your Italian Naturally account</strong>,
							with the subject <em>“Delete my data.”</em>
						</li>
						<li>
							Tell us whether you want <strong>all</strong> of your data deleted
							(your whole account) or only <strong>specific</strong> data.
						</li>
						<li>
							We verify you own the account and complete the deletion, then reply to
							confirm.
						</li>
					</ol>
					<p>
						Your request is completed within <strong>30 days</strong>. (An in-app
						option is coming; until then, the email request above is the way.)
					</p>
				</div>

				<h2 style={styles.h2}>What is deleted</h2>
				<ul>
					<li>Account identifiers — email address and, if provided, your name (and any Google sign-in linkage).</li>
					<li>Learning data — conversations and lines you&apos;ve played, your pronunciation (Capeesh) scores, and your spaced-repetition practice history.</li>
					<li>Subscription/entitlement records held on our servers.</li>
					<li>Support emails you&apos;ve sent us (on request).</li>
				</ul>

				<h2 style={styles.h2}>What is kept, and for how long</h2>
				<ul>
					<li>
						<strong>Voice recordings — none to delete.</strong> Pronunciation scoring
						runs on your device; recordings are never uploaded to or stored by us.
					</li>
					<li>
						<strong>Payment / card data — we never hold it.</strong> Apple and Google
						process all payments and retain purchase records under their own policies;
						manage or cancel a subscription in your Apple or Google account settings.
					</li>
					<li>
						<strong>Legally required records.</strong> We keep only what we&apos;re
						required to retain by law, and only for as long as the law requires.
					</li>
					<li>
						<strong>Backups.</strong> Residual copies in our encrypted backups are
						purged within an additional <strong>30 days</strong>.
					</li>
				</ul>

				<h2 style={styles.h2}>Contact</h2>
				<p>
					Questions about deletion or this policy? Email{' '}
					<a style={styles.a} href="mailto:abogaard.dev@gmail.com">abogaard.dev@gmail.com</a>.
					See also our <Link to="/privacy" style={styles.a}>Privacy Policy</Link> and{' '}
					<Link to="/delete_account" style={styles.a}>account-deletion page</Link>.
				</p>
			</div>
		</div>
	);
};

export default DeleteData;
